import React, { useEffect, useState, useRef, cloneElement } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { ShieldIcon, UsersIcon, BriefcaseIcon, CheckIcon, ClockIcon, ArrowRightIcon, StarIcon, UsersRoundIcon, BookOpenIcon, TrendingUpIcon, AwardIcon, GraduationCapIcon, BarChartIcon, PlayCircleIcon, ChevronRightIcon, BadgeCheckIcon, MousePointerClickIcon } from 'lucide-react';
export const Homepage2 = () => {
  return <div className="flex flex-col w-full min-h-screen bg-white text-charcoal" data-id="element-2937">
      <Header data-id="element-2938" />
      <main className="flex-grow" data-id="element-2939">
        <HeroSection2 data-id="element-2940" />
        <CourseCategories data-id="element-2941" />
        <FeaturedCourses data-id="element-2942" />
        <TrainingStats data-id="element-2943" />
        <PopularCourses data-id="element-2944" />
        <TestimonialsSection data-id="element-2945" />
        <EnterpriseTraining data-id="element-2946" />
        <CtaBanner2 data-id="element-2947" />
      </main>
      <Footer data-id="element-2948" />
    </div>;
};
const HeroSection2 = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="relative pt-28 pb-24 md:pt-36 md:pb-32 lg:pt-44 lg:pb-40 opacity-0 transition-opacity duration-1000 overflow-hidden" data-id="element-2949">
      {/* Background with parallax effect */}
      <div className="absolute inset-0 z-0" data-id="element-2950">
        <div className="absolute inset-0 bg-cover bg-center transform scale-110 transition-transform duration-3000" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')",
        transform: isVisible ? 'scale(1)' : 'scale(1.1)'
      }} data-id="element-2951"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/90 via-charcoal/80 to-charcoal/95" data-id="element-2952"></div>
      </div>
      {/* Animated elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-30" data-id="element-2953">
        <div className="absolute top-10 left-10 w-40 h-40 rounded-full border-8 border-teal/20 transform transition-all duration-1000" style={{
        transform: isVisible ? 'translateY(0) rotate(0deg)' : 'translateY(-100px) rotate(-45deg)',
        opacity: isVisible ? 1 : 0,
        transitionDelay: '0.3s'
      }} data-id="element-2954"></div>
        <div className="absolute bottom-20 right-20 w-60 h-60 rounded-full border-8 border-aqua/10 transform transition-all duration-1000" style={{
        transform: isVisible ? 'translateY(0) rotate(0deg)' : 'translateY(100px) rotate(45deg)',
        opacity: isVisible ? 1 : 0,
        transitionDelay: '0.5s'
      }} data-id="element-2955"></div>
        <div className="absolute top-1/3 right-1/4 w-20 h-20 rounded-full border-4 border-white/30 transform transition-all duration-1000" style={{
        transform: isVisible ? 'translateX(0)' : 'translateX(100px)',
        opacity: isVisible ? 1 : 0,
        transitionDelay: '0.7s'
      }} data-id="element-2956"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2957">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2958">
          <div className="inline-block px-4 py-1.5 rounded-full bg-teal/20 backdrop-blur-sm text-aqua text-sm font-medium mb-6 transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          opacity: isVisible ? 1 : 0
        }} data-id="element-2959">
            Trusted by 10,000+ professionals across Europe
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8 text-white tracking-tight transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          opacity: isVisible ? 1 : 0,
          transitionDelay: '0.2s'
        }} data-id="element-2960">
            Elevate Your Skills. <br className="hidden sm:block" data-id="element-2961" />
            <span className="text-gradient bg-gradient-to-r from-teal via-aqua to-teal bg-clip-text text-transparent" data-id="element-2962">
              Advance Your Career.
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          opacity: isVisible ? 1 : 0,
          transitionDelay: '0.4s'
        }} data-id="element-2963">
            Industry-leading training courses designed by experts and trusted by
            businesses of every size. Get certified and stay ahead.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-5 transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          opacity: isVisible ? 1 : 0,
          transitionDelay: '0.6s'
        }} data-id="element-2964">
            <a href="/courses" className="group px-8 py-4 bg-teal text-white font-medium rounded-md transition-all duration-300 shadow-lg transform hover:-translate-y-1 hover:shadow-xl relative overflow-hidden" data-id="element-2965">
              <span className="relative z-10" data-id="element-2966">Explore Courses</span>
              <div className="absolute inset-0 bg-gradient-to-r from-teal to-aqua opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2967"></div>
            </a>
            <a href="/demo" className="group px-8 py-4 bg-transparent text-white font-medium rounded-md border-2 border-white hover:border-aqua transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center" data-id="element-2968">
              <PlayCircleIcon className="h-5 w-5 mr-2 group-hover:text-aqua transition-colors" data-id="element-2969" />
              <span className="group-hover:text-aqua transition-colors" data-id="element-2970">
                Watch Demo
              </span>
            </a>
          </div>
          <div className="mt-16 flex flex-wrap justify-center gap-8 transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          opacity: isVisible ? 1 : 0,
          transitionDelay: '0.8s'
        }} data-id="element-2971">
            <div className="flex items-center" data-id="element-2972">
              <BadgeCheckIcon className="h-5 w-5 text-aqua mr-2" data-id="element-2973" />
              <span className="text-white/80" data-id="element-2974">Accredited Certificates</span>
            </div>
            <div className="flex items-center" data-id="element-2975">
              <BadgeCheckIcon className="h-5 w-5 text-aqua mr-2" data-id="element-2976" />
              <span className="text-white/80" data-id="element-2977">Expert Instructors</span>
            </div>
            <div className="flex items-center" data-id="element-2978">
              <BadgeCheckIcon className="h-5 w-5 text-aqua mr-2" data-id="element-2979" />
              <span className="text-white/80" data-id="element-2980">Flexible Learning</span>
            </div>
          </div>
        </div>
      </div>
      {/* Animated scroll indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center transition-opacity duration-1000" style={{
      opacity: isVisible ? 1 : 0,
      transitionDelay: '1s'
    }} data-id="element-2981">
        <span className="text-white/60 text-sm mb-2" data-id="element-2982">Scroll to explore</span>
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-1" data-id="element-2983">
          <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce mt-1" data-id="element-2984"></div>
        </div>
      </div>
    </section>;
};
const CourseCategories = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const categories = [{
    icon: <ShieldIcon size={32} className="text-white" data-id="element-2985" />,
    title: 'Compliance Training',
    description: 'Essential courses to meet regulatory requirements and industry standards',
    count: '40+ Courses',
    color: 'from-blue-600 to-blue-700',
    link: '/compliance'
  }, {
    icon: <UsersIcon size={32} className="text-white" data-id="element-2986" />,
    title: 'Workplace & HR',
    description: 'Build stronger teams with essential workplace and HR training',
    count: '35+ Courses',
    color: 'from-purple-600 to-purple-700',
    link: '/workplace'
  }, {
    icon: <BriefcaseIcon size={32} className="text-white" data-id="element-2987" />,
    title: 'Business Skills',
    description: 'Enhance your professional capabilities and business acumen',
    count: '50+ Courses',
    color: 'from-teal to-teal/90',
    link: '/business'
  }];
  return <section ref={sectionRef} className="py-20 bg-white opacity-0 transition-opacity duration-1000" data-id="element-2988">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2989">
        <div className="max-w-4xl mx-auto mb-16 text-center" data-id="element-2990">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-2991">
            <BookOpenIcon className="h-6 w-6 text-teal" data-id="element-2992" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-2993">
            Training That Transforms
          </h2>
          <p className="text-xl text-gray-600" data-id="element-2994">
            Explore our comprehensive range of training categories designed to
            elevate your skills
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-2995">
          {categories.map((category, index) => <div key={index} className="group rounded-2xl overflow-hidden shadow-lg transition-all duration-500 transform hover:-translate-y-2 hover:shadow-xl" data-id="element-2996">
              <div className={`bg-gradient-to-br ${category.color} p-8`} data-id="element-2997">
                <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20" data-id="element-2998">
                  {category.icon}
                </div>
                <h3 className="text-2xl font-bold mb-2 text-white" data-id="element-2999">
                  {category.title}
                </h3>
                <p className="text-white/80 mb-6" data-id="element-3000">{category.description}</p>
                <div className="flex items-center justify-between" data-id="element-3001">
                  <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm" data-id="element-3002">
                    {category.count}
                  </span>
                  <a href={category.link} className="inline-flex items-center text-white font-medium group-hover:translate-x-1 transition-transform" data-id="element-3003">
                    Explore Courses
                    <ChevronRightIcon className="h-5 w-5 ml-1" data-id="element-3004" />
                  </a>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const FeaturedCourses = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const featuredCourse = {
    title: 'Food Safety Level 2',
    badge: 'Most Popular',
    description: 'Comprehensive food safety training for all food handlers. Meets EU and UK standards for compliance and best practices.',
    image: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    rating: 4.9,
    reviews: 1250,
    students: '12,450+',
    duration: '2 hours',
    modules: 8,
    features: ['Accredited certification', 'Interactive learning', 'Self-paced', '24/7 support']
  };
  return <section ref={sectionRef} className="py-24 bg-gray-50 opacity-0 transition-opacity duration-1000 relative overflow-hidden" data-id="element-3005">
      {/* Background patterns */}
      <div className="absolute inset-0 z-0 opacity-30" data-id="element-3006">
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-teal/10" data-id="element-3007"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-aqua/10" data-id="element-3008"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-3009">
        <div className="max-w-4xl mx-auto mb-16 text-center" data-id="element-3010">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-3011">
            <AwardIcon className="h-6 w-6 text-teal" data-id="element-3012" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-3013">
            Featured Course
          </h2>
          <p className="text-xl text-gray-600" data-id="element-3014">
            Our most popular and highly-rated training experience
          </p>
        </div>
        <div className="max-w-6xl mx-auto" data-id="element-3015">
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100" data-id="element-3016">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-3017">
              <div className="relative" data-id="element-3018">
                <img src={featuredCourse.image} alt={featuredCourse.title} className="w-full h-full object-cover object-center" style={{
                minHeight: '400px'
              }} data-id="element-3019" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" data-id="element-3020"></div>
                <div className="absolute top-6 left-6" data-id="element-3021">
                  <span className="px-4 py-2 bg-aqua/90 backdrop-blur-sm text-white font-medium rounded-full inline-block" data-id="element-3022">
                    {featuredCourse.badge}
                  </span>
                </div>
                <div className="absolute bottom-6 left-6 right-6" data-id="element-3023">
                  <div className="flex items-center text-white mb-2" data-id="element-3024">
                    <div className="flex" data-id="element-3025">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-5 w-5 ${i < Math.floor(featuredCourse.rating) ? 'text-amber-400' : 'text-white/30'}`} data-id="element-3026" />)}
                    </div>
                    <span className="ml-2 font-medium" data-id="element-3027">
                      {featuredCourse.rating}
                    </span>
                    <span className="mx-2" data-id="element-3028">•</span>
                    <span data-id="element-3029">{featuredCourse.reviews} reviews</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white" data-id="element-3030">
                    {featuredCourse.title}
                  </h3>
                </div>
              </div>
              <div className="p-8 md:p-10 flex flex-col" data-id="element-3031">
                <div className="mb-6" data-id="element-3032">
                  <p className="text-xl text-gray-600 mb-6" data-id="element-3033">
                    {featuredCourse.description}
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-3034">
                    <div className="flex items-center" data-id="element-3035">
                      <UsersRoundIcon className="h-5 w-5 text-teal mr-2" data-id="element-3036" />
                      <span data-id="element-3037">{featuredCourse.students} students</span>
                    </div>
                    <div className="flex items-center" data-id="element-3038">
                      <ClockIcon className="h-5 w-5 text-teal mr-2" data-id="element-3039" />
                      <span data-id="element-3040">{featuredCourse.duration}</span>
                    </div>
                    <div className="flex items-center" data-id="element-3041">
                      <BookOpenIcon className="h-5 w-5 text-teal mr-2" data-id="element-3042" />
                      <span data-id="element-3043">{featuredCourse.modules} modules</span>
                    </div>
                    <div className="flex items-center" data-id="element-3044">
                      <AwardIcon className="h-5 w-5 text-teal mr-2" data-id="element-3045" />
                      <span data-id="element-3046">Certificate included</span>
                    </div>
                  </div>
                  <div className="mb-8" data-id="element-3047">
                    <h4 className="text-lg font-bold mb-3 text-charcoal" data-id="element-3048">
                      Course Features:
                    </h4>
                    <ul className="space-y-2" data-id="element-3049">
                      {featuredCourse.features.map((feature, index) => <li key={index} className="flex items-center" data-id="element-3050">
                          <CheckIcon className="h-5 w-5 text-teal mr-2 flex-shrink-0" data-id="element-3051" />
                          <span data-id="element-3052">{feature}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
                <div className="mt-auto flex flex-col sm:flex-row gap-4" data-id="element-3053">
                  <a href="/courses/food-safety-level-2" className="px-8 py-4 bg-teal text-white font-medium rounded-md hover:bg-gradient-to-r hover:from-teal hover:to-aqua transition-all duration-300 text-center shadow-md hover:shadow-lg transform hover:-translate-y-1" data-id="element-3054">
                    Enroll Now
                  </a>
                  <a href="/courses/food-safety-level-2/preview" className="px-8 py-4 bg-transparent text-teal font-medium rounded-md border-2 border-teal hover:bg-teal/5 transition-all duration-300 text-center transform hover:-translate-y-1" data-id="element-3055">
                    Preview Course
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const TrainingStats = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const stats = [{
    value: '125+',
    label: 'Training Courses',
    icon: <BookOpenIcon className="h-8 w-8 text-white" data-id="element-3056" />
  }, {
    value: '50,000+',
    label: 'Certified Professionals',
    icon: <AwardIcon className="h-8 w-8 text-white" data-id="element-3057" />
  }, {
    value: '98%',
    label: 'Satisfaction Rate',
    icon: <StarIcon className="h-8 w-8 text-white" data-id="element-3058" />
  }, {
    value: '24/7',
    label: 'Learning Support',
    icon: <UsersRoundIcon className="h-8 w-8 text-white" data-id="element-3059" />
  }];
  return <section ref={sectionRef} className="py-20 bg-gradient-to-br from-charcoal to-charcoal/90 opacity-0 transition-opacity duration-1000 relative overflow-hidden" data-id="element-3060">
      {/* Background pattern */}
      <div className="absolute inset-0 z-0 opacity-10" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }} data-id="element-3061"></div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-3062">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-3063">
          {stats.map((stat, index) => <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center transform transition-all duration-500 hover:-translate-y-2" data-id="element-3064">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal mb-4" data-id="element-3065">
                {stat.icon}
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 transition-all duration-1000" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: `${index * 0.1 + 0.3}s`
          }} data-id="element-3066">
                {stat.value}
              </div>
              <p className="text-white/80 text-lg" data-id="element-3067">{stat.label}</p>
            </div>)}
        </div>
      </div>
    </section>;
};
const PopularCourses = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const courses = [{
    title: 'Health & Safety Essentials',
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Essential',
    description: 'Core health and safety principles for workplace compliance and risk management.',
    students: '8,320+',
    duration: '3 hours',
    icon: <ShieldIcon size={20} data-id="element-3068" />,
    color: 'bg-blue-500'
  }, {
    title: 'Leadership Excellence',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Top Rated',
    description: 'Develop essential leadership skills to inspire and guide your team effectively.',
    students: '9,120+',
    duration: '4 hours',
    icon: <UsersIcon size={20} data-id="element-3069" />,
    color: 'bg-indigo-500'
  }, {
    title: 'Digital Marketing Masterclass',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1115&q=80',
    badge: 'Bestseller',
    description: 'Master the fundamentals of digital marketing across multiple platforms.',
    students: '10,780+',
    duration: '5 hours',
    icon: <TrendingUpIcon size={20} data-id="element-3070" />,
    color: 'bg-rose-500'
  }, {
    title: 'GDPR Fundamentals',
    image: 'https://images.unsplash.com/photo-1563906267088-b029e7101114?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Updated',
    description: 'Stay compliant with the latest data protection regulations and best practices.',
    students: '7,890+',
    duration: '1.5 hours',
    icon: <CheckIcon size={20} data-id="element-3071" />,
    color: 'bg-green-500'
  }, {
    title: 'Cybersecurity Fundamentals',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Critical',
    description: 'Protect your business with essential cybersecurity knowledge and practices.',
    students: '8,450+',
    duration: '2 hours',
    icon: <ShieldIcon size={20} data-id="element-3072" />,
    color: 'bg-cyan-500'
  }, {
    title: 'Workplace Diversity & Inclusion',
    image: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Essential',
    description: 'Create an inclusive workplace culture that values diversity and promotes equality.',
    students: '6,780+',
    duration: '2 hours',
    icon: <UsersRoundIcon size={20} data-id="element-3073" />,
    color: 'bg-purple-500'
  }];
  return <section ref={sectionRef} className="py-24 bg-white opacity-0 transition-opacity duration-1000" data-id="element-3074">
      <div className="container mx-auto px-4 md:px-6" data-id="element-3075">
        <div className="max-w-4xl mx-auto mb-16 text-center" data-id="element-3076">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-3077">
            <StarIcon className="h-6 w-6 text-teal" data-id="element-3078" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-3079">
            Most Popular Courses
          </h2>
          <p className="text-xl text-gray-600" data-id="element-3080">
            Discover our highest-rated and most-enrolled training courses
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3081">
          {courses.map((course, index) => <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl flex flex-col h-full" data-id="element-3082">
              <div className="relative h-48" data-id="element-3083">
                <img src={course.image} alt={course.title} className="w-full h-full object-cover" data-id="element-3084" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-3085"></div>
                <div className="absolute top-4 right-4" data-id="element-3086">
                  <span className="px-3 py-1 bg-aqua/80 backdrop-blur-sm text-white text-sm font-medium rounded-full" data-id="element-3087">
                    {course.badge}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 flex items-center text-white" data-id="element-3088">
                  <div className={`h-8 w-8 rounded-full ${course.color} flex items-center justify-center mr-2`} data-id="element-3089">
                    {cloneElement(course.icon, {
                  className: 'text-white'
                })}
                  </div>
                  <div className="flex items-center text-white/90 text-sm" data-id="element-3090">
                    <UsersRoundIcon className="h-3.5 w-3.5 mr-1" data-id="element-3091" />
                    <span data-id="element-3092">{course.students}</span>
                  </div>
                </div>
              </div>
              <div className="p-6 flex-grow flex flex-col" data-id="element-3093">
                <h3 className="text-xl font-bold mb-2 text-charcoal" data-id="element-3094">
                  {course.title}
                </h3>
                <p className="text-gray-600 mb-4 flex-grow" data-id="element-3095">
                  {course.description}
                </p>
                <div className="flex items-center justify-between mt-auto" data-id="element-3096">
                  <div className="flex items-center text-gray-500" data-id="element-3097">
                    <ClockIcon className="h-4 w-4 mr-1" data-id="element-3098" />
                    <span className="text-sm" data-id="element-3099">{course.duration}</span>
                  </div>
                  <a href={`/courses/${course.title.toLowerCase().replace(/\s+/g, '-')}`} className="text-teal font-medium hover:text-aqua transition-colors flex items-center" data-id="element-3100">
                    View Course
                    <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-3101" />
                  </a>
                </div>
              </div>
            </div>)}
        </div>
        <div className="text-center mt-12" data-id="element-3102">
          <a href="/courses" className="inline-flex items-center px-8 py-4 bg-teal text-white font-medium rounded-md hover:bg-gradient-to-r hover:from-teal hover:to-aqua transition-all duration-300 shadow-lg transform hover:-translate-y-1" data-id="element-3103">
            Browse All Courses
            <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-3104" />
          </a>
        </div>
      </div>
    </section>;
};
const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const testimonials = [{
    quote: 'The food safety training was incredibly comprehensive and engaging. I feel much more confident in my role now.',
    name: 'Sarah Johnson',
    title: 'Restaurant Manager',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
    rating: 5
  }, {
    quote: 'The leadership course transformed how I manage my team. The practical strategies were easy to implement right away.',
    name: 'Michael Chen',
    title: 'Operations Director',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    rating: 5
  }, {
    quote: 'As a business owner, the GDPR training was invaluable. Clear, concise and exactly what we needed for compliance.',
    name: 'Emma Richards',
    title: 'CEO, TechStart',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=761&q=80',
    rating: 5
  }];
  return <section ref={sectionRef} className="py-24 bg-gray-50 opacity-0 transition-opacity duration-1000" data-id="element-3105">
      <div className="container mx-auto px-4 md:px-6" data-id="element-3106">
        <div className="max-w-4xl mx-auto mb-16 text-center" data-id="element-3107">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-3108">
            <UsersRoundIcon className="h-6 w-6 text-teal" data-id="element-3109" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-3110">
            Success Stories
          </h2>
          <p className="text-xl text-gray-600" data-id="element-3111">
            Hear from professionals who transformed their careers with our
            training
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3112">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl flex flex-col" data-id="element-3113">
              <div className="p-8 flex-grow flex flex-col" data-id="element-3114">
                <div className="flex mb-6" data-id="element-3115">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-5 w-5 ${i < testimonial.rating ? 'text-amber-400' : 'text-gray-200'}`} data-id="element-3116" />)}
                </div>
                <blockquote className="text-gray-600 italic mb-6 flex-grow" data-id="element-3117">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center mt-auto" data-id="element-3118">
                  <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover mr-4" data-id="element-3119" />
                  <div data-id="element-3120">
                    <p className="font-bold text-charcoal" data-id="element-3121">
                      {testimonial.name}
                    </p>
                    <p className="text-gray-500 text-sm" data-id="element-3122">{testimonial.title}</p>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const EnterpriseTraining = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const features = [{
    icon: <UsersRoundIcon className="h-5 w-5 text-teal" data-id="element-3123" />,
    title: 'Team Management',
    description: 'Assign and track training for your entire organization'
  }, {
    icon: <BarChartIcon className="h-5 w-5 text-teal" data-id="element-3124" />,
    title: 'Advanced Reporting',
    description: 'Detailed analytics and completion tracking'
  }, {
    icon: <AwardIcon className="h-5 w-5 text-teal" data-id="element-3125" />,
    title: 'Custom Branding',
    description: 'Your own branded learning portal'
  }, {
    icon: <CheckIcon className="h-5 w-5 text-teal" data-id="element-3126" />,
    title: 'Bulk Discounts',
    description: 'Special pricing for volume licenses'
  }];
  return <section ref={sectionRef} className="py-24 bg-white opacity-0 transition-opacity duration-1000" data-id="element-3127">
      <div className="container mx-auto px-4 md:px-6" data-id="element-3128">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto" data-id="element-3129">
          <div data-id="element-3130">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-3131">
              <BriefcaseIcon className="h-6 w-6 text-teal" data-id="element-3132" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-3133">
              Enterprise Training Solutions
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-3134">
              Comprehensive training solutions for teams and organizations of
              all sizes. Scale your training efforts with our enterprise
              platform.
            </p>
            <div className="space-y-6 mb-10" data-id="element-3135">
              {features.map((feature, index) => <div key={index} className="flex items-start" data-id="element-3136">
                  <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-3137">
                    {feature.icon}
                  </div>
                  <div data-id="element-3138">
                    <h3 className="font-bold text-charcoal text-lg mb-1" data-id="element-3139">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-3140">{feature.description}</p>
                  </div>
                </div>)}
            </div>
            <a href="/enterprise" className="inline-flex items-center px-8 py-4 bg-teal text-white font-medium rounded-md hover:bg-gradient-to-r hover:from-teal hover:to-aqua transition-all duration-300 shadow-lg transform hover:-translate-y-1" data-id="element-3141">
              Learn More
              <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-3142" />
            </a>
          </div>
          <div className="relative" data-id="element-3143">
            <div className="rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_20px_50px_rgba(11,117,122,0.2)]" data-id="element-3144">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Enterprise Training Dashboard" className="w-full h-full object-cover" data-id="element-3145" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" data-id="element-3146"></div>
            </div>
            <div className="absolute -bottom-8 -left-8 bg-white p-5 rounded-lg shadow-xl" data-id="element-3147">
              <div className="flex items-center" data-id="element-3148">
                <div className="h-4 w-4 rounded-full bg-aqua mr-3 animate-pulse" data-id="element-3149"></div>
                <span className="text-charcoal font-bold text-lg" data-id="element-3150">
                  Team Dashboard
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const CtaBanner2 = () => {
  return <section className="py-20 relative overflow-hidden" data-id="element-3151">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal to-teal/80 z-0" style={{
      backgroundImage: `
            linear-gradient(to bottom right, #0b757a, #085c60),
            url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
          `
    }} data-id="element-3152"></div>
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-10" data-id="element-3153">
        <div className="absolute top-10 left-10 w-40 h-40 rounded-full border-8 border-white/20" data-id="element-3154"></div>
        <div className="absolute bottom-20 right-20 w-60 h-60 rounded-full border-8 border-white/10" data-id="element-3155"></div>
        <div className="absolute top-1/2 right-1/3 w-20 h-20 rounded-full border-4 border-white/30" data-id="element-3156"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-3157">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3158">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-6" data-id="element-3159">
            <GraduationCapIcon className="h-8 w-8 text-white" data-id="element-3160" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight" data-id="element-3161">
            Start Your Learning Journey Today
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto" data-id="element-3162">
            Join thousands of professionals who have transformed their careers
            with our industry-leading training courses.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-3163">
            <a href="/courses" className="group px-10 py-4 bg-white text-teal font-medium rounded-md transition-all duration-300 shadow-xl transform hover:-translate-y-1 hover:shadow-2xl relative overflow-hidden" data-id="element-3164">
              <span className="relative z-10" data-id="element-3165">Browse Courses</span>
              <div className="absolute bottom-0 left-0 w-full h-0 bg-gray-50 transition-all duration-300 group-hover:h-full -z-0" data-id="element-3166"></div>
            </a>
            <a href="/contact" className="px-10 py-4 bg-transparent text-white font-medium rounded-md border-2 border-white hover:bg-white/10 transition-all duration-300 transform hover:-translate-y-1" data-id="element-3167">
              Contact Sales
            </a>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-10" data-id="element-3168">
            <div className="flex flex-col items-center" data-id="element-3169">
              <div className="text-white/80 mb-2" data-id="element-3170">Accredited by</div>
              <div className="text-white font-medium text-lg" data-id="element-3171">
                CPD Certified
              </div>
            </div>
            <div className="flex flex-col items-center" data-id="element-3172">
              <div className="text-white/80 mb-2" data-id="element-3173">Trusted by</div>
              <div className="text-white font-medium text-lg" data-id="element-3174">
                1000+ Businesses
              </div>
            </div>
            <div className="flex flex-col items-center" data-id="element-3175">
              <div className="text-white/80 mb-2" data-id="element-3176">Rated</div>
              <div className="text-white font-medium text-lg flex items-center" data-id="element-3177">
                <span className="mr-1" data-id="element-3178">4.9/5</span>
                <div className="flex" data-id="element-3179">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-white" data-id="element-3180" />)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};